import React, { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'

const getProjectRefFromUrl = (supabaseUrl) => {
  try {
    const hostname = new URL(supabaseUrl).hostname
    // <ref>.supabase.co
    return hostname.split('.')[0] || null
  } catch {
    return null
  }
}

const generateSecret = () => {
  const bytes = new Uint8Array(32)
  window.crypto.getRandomValues(bytes)
  // base64url-ish
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '')
}

const normalizeWebhookConfig = (row) => {
  if (!row) return null
  const cfg = Array.isArray(row.webhook_config)
    ? row.webhook_config[0]
    : row.webhook_config
  return cfg || null
}

const AutomationIntegrationsModal = ({ isOpen, customer, onClose }) => {
  const businessId = customer?.id

  const projectRef = useMemo(() => {
    return getProjectRefFromUrl(process.env.REACT_APP_SUPABASE_URL)
  }, [])

  const [loading, setLoading] = useState(false)
  const [integrations, setIntegrations] = useState([])
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const [formData, setFormData] = useState({
    name: '',
    integrationType: '',
  })

  const webhookBaseUrl = projectRef
    ? `https://${projectRef}.functions.supabase.co/automation-webhook`
    : null

  const fetchIntegrations = async () => {
    if (!businessId) return

    setLoading(true)
    setError('')

    try {
      const { data, error: fetchError } = await supabase
        .from('automation_integrations')
        .select(
          `
            id,
            business_id,
            name,
            integration_type,
            is_active,
            created_at,
            webhook_config:automation_webhook_configs (
              id,
              webhook_id,
              secret,
              is_active,
              created_at,
              rotated_at,
              last_received_at
            )
          `
        )
        .eq('business_id', businessId)
        .order('created_at', { ascending: true })

      if (fetchError) throw fetchError

      const normalized = (data || []).map((row) => ({
        ...row,
        webhook_config: normalizeWebhookConfig(row),
      }))

      setIntegrations(normalized)
    } catch (err) {
      console.error('Failed to fetch integrations:', err)
      setError('Failed to fetch automation integrations')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return
    fetchIntegrations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, businessId])

  const handleCreateIntegration = async (e) => {
    e.preventDefault()
    if (!businessId) return

    if (!formData.name.trim() || !formData.integrationType.trim()) {
      setError('Integration name and integration type are required')
      return
    }

    setLoading(true)
    setError('')

    try {
      const { error: insertError } = await supabase
        .from('automation_integrations')
        .insert({
          business_id: businessId,
          name: formData.name.trim(),
          integration_type: formData.integrationType.trim(),
          is_active: true,
        })

      if (insertError) throw insertError

      setFormData({ name: '', integrationType: '' })
      setSuccessMessage('Integration created')
      setTimeout(() => setSuccessMessage(''), 2500)
      await fetchIntegrations()
    } catch (err) {
      console.error('Create integration failed:', err)
      setError('Failed to create integration')
    } finally {
      setLoading(false)
    }
  }

  const handleRotateWebhook = async (integration) => {
    if (!integration?.id) return

    setLoading(true)
    setError('')

    try {
      const secret = generateSecret()
      const now = new Date().toISOString()

      const payload = {
        integration_id: integration.id,
        secret,
        is_active: true,
        rotated_at: integration.webhook_config ? now : null,
      }

      const { error: upsertError } = await supabase
        .from('automation_webhook_configs')
        .upsert(payload, { onConflict: 'integration_id' })

      if (upsertError) throw upsertError

      setSuccessMessage(integration.webhook_config ? 'Secret rotated' : 'Webhook configured')
      setTimeout(() => setSuccessMessage(''), 2500)
      await fetchIntegrations()
    } catch (err) {
      console.error('Rotate webhook failed:', err)
      setError('Failed to generate/rotate webhook secret')
    } finally {
      setLoading(false)
    }
  }

  const handleDisableWebhook = async (integration) => {
    if (!integration?.id) return

    setLoading(true)
    setError('')

    try {
      const { error: updateError } = await supabase
        .from('automation_webhook_configs')
        .update({ is_active: false })
        .eq('integration_id', integration.id)

      if (updateError) throw updateError

      setSuccessMessage('Webhook disabled')
      setTimeout(() => setSuccessMessage(''), 2500)
      await fetchIntegrations()
    } catch (err) {
      console.error('Disable webhook failed:', err)
      setError('Failed to disable webhook')
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = async (value) => {
    try {
      await navigator.clipboard.writeText(value)
      setSuccessMessage('Copied to clipboard')
      setTimeout(() => setSuccessMessage(''), 1500)
    } catch {
      // ignore
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-screen overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Automation Integrations</h2>
            <p className="text-sm text-gray-600 mt-1">
              {customer?.business_name || 'Customer'} ({customer?.owner_email || '—'})
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-800 rounded">
            {successMessage}
          </div>
        )}

        {!webhookBaseUrl && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded">
            Could not determine your Supabase project ref from REACT_APP_SUPABASE_URL. Webhook URLs
            will not be shown.
          </div>
        )}

        <div className="border border-gray-200 rounded-md p-4 mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Create Integration</h3>
          <form onSubmit={handleCreateIntegration} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              placeholder="Integration name (e.g. MailerLite → HubSpot)"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={formData.integrationType}
              onChange={(e) => setFormData((p) => ({ ...p, integrationType: e.target.value }))}
              placeholder="integration_type (e.g. mailerlite_hubspot)"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
            >
              {loading ? 'Saving…' : 'Create'}
            </button>
          </form>
          <p className="text-xs text-gray-500 mt-2">
            Templates are keyed by <span className="font-mono">integration_type</span> +{' '}
            <span className="font-mono">event_key</span>.
          </p>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Integrations</h3>

          {loading && integrations.length === 0 ? (
            <div className="text-sm text-gray-600">Loading…</div>
          ) : integrations.length === 0 ? (
            <div className="text-sm text-gray-600">No integrations yet.</div>
          ) : (
            <div className="space-y-3">
              {integrations.map((integration) => {
                const cfg = integration.webhook_config
                const webhookUrl =
                  webhookBaseUrl && cfg?.webhook_id
                    ? `${webhookBaseUrl}/${cfg.webhook_id}`
                    : null

                return (
                  <div key={integration.id} className="border border-gray-200 rounded-md p-4">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-gray-900">{integration.name}</div>
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                            {integration.integration_type}
                          </span>
                          {!integration.is_active && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                              Inactive
                            </span>
                          )}
                        </div>
                        {cfg?.last_received_at && (
                          <div className="text-xs text-gray-500 mt-1">
                            Last received: {new Date(cfg.last_received_at).toLocaleString()}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleRotateWebhook(integration)}
                          disabled={loading}
                          className="text-sm px-3 py-1.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                        >
                          {cfg ? 'Rotate Secret' : 'Generate Webhook'}
                        </button>
                        {cfg?.is_active && (
                          <button
                            type="button"
                            onClick={() => handleDisableWebhook(integration)}
                            disabled={loading}
                            className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            Disable
                          </button>
                        )}
                      </div>
                    </div>

                    {cfg ? (
                      <div className="mt-3 space-y-2">
                        <div className="text-xs text-gray-500">
                          Webhook status:{' '}
                          <span className={cfg.is_active ? 'text-green-700' : 'text-red-700'}>
                            {cfg.is_active ? 'Active' : 'Disabled'}
                          </span>
                        </div>

                        {webhookUrl && (
                          <div className="text-sm">
                            <div className="text-xs text-gray-500 mb-1">Webhook URL</div>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs overflow-x-auto">
                                {webhookUrl}
                              </code>
                              <button
                                type="button"
                                className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                                onClick={() => copyToClipboard(webhookUrl)}
                              >
                                Copy
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="text-sm">
                          <div className="text-xs text-gray-500 mb-1">Secret (X-Automation-Secret)</div>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs overflow-x-auto">
                              {cfg.secret}
                            </code>
                            <button
                              type="button"
                              className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                              onClick={() => copyToClipboard(cfg.secret)}
                            >
                              Copy
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 text-sm text-gray-600">
                        No webhook configured yet.
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default AutomationIntegrationsModal
